import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import { 
  getAllSlots,
  blockSlot, 
  unBlock, 
  editIndividual,
  getAllFacilities,
  getAllSports,
  getSlotsByDate
} from '../services/api';

const initialState = {
  scheduleList: [],
  filterScheduleList: [],
  loading: false,
  selectedDate: moment(new Date()).format('MM-DD-YYYY'),
  selectedFacility: null,
  selectedSport: null,
  selectedPitch: null,
  statusFilter: null,
  facilities: [],
  sports: [],
  pitches: [],
  error: null,
  weeklySlots: [],
  weeklyLoading: false
};

export const fetchAllSlots = createAsyncThunk(
  'schedule/fetchAllSlots',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllSlots();
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const fetchWeeklySlots = createAsyncThunk(
  'schedule/fetchWeeklySlots',
  async ({ slotDetailId, fromDate, slots }, { rejectWithValue }) => {
    try {
      // If slots are provided directly, use them
      if (slots) {
        return { status: true, slots };
      }
      
      // Otherwise, fetch from API
      const response = await getSlotsByDate({
        slot_detail_id: slotDetailId,
        from_date: fromDate
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const blockSlotAction = createAsyncThunk(
  'schedule/blockSlot',
  async ({ detailSlotId, slotId }, { rejectWithValue, dispatch }) => {
    try {
      await blockSlot({ detailSlotId, slotId });
      dispatch(fetchAllSlots());
      return { detailSlotId, slotId };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const unblockSlotAction = createAsyncThunk(
  'schedule/unblockSlot',
  async ({ detailSlotId, slotId }, { rejectWithValue, dispatch }) => {
    try {
      await unBlock({ detailSlotId, slotId });
      dispatch(fetchAllSlots());
      return { detailSlotId, slotId };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const editSlotPriceAction = createAsyncThunk(
  'schedule/editSlotPrice',
  async ({ detailSlotId, slotId, price }, { rejectWithValue, dispatch }) => {
    try {
      await editIndividual({ detailSlotId, slotId, price });
      dispatch(fetchAllSlots());
      return { detailSlotId, slotId, price };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

export const fetchFacilitiesAndSports = createAsyncThunk(
  'schedule/fetchFacilitiesAndSports',
  async (_, { rejectWithValue }) => {
    try {
      const [facilitiesRes, sportsRes] = await Promise.all([
        getAllFacilities(),
        getAllSports()
      ]);
      return {
        facilities: facilitiesRes.data.facilities,
        sports: sportsRes.data.sports
      };
    } catch (err) {
      return rejectWithValue(err.response.data);
    }
  }
);

const scheduleSlice = createSlice({
  name: 'schedule',
  initialState,
  reducers: {
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setSelectedFacility: (state, action) => {
      state.selectedFacility = action.payload;
      // Reset sport and pitch when facility changes
      state.selectedSport = null;
      state.selectedPitch = null;
    },
    setSelectedSport: (state, action) => {
      state.selectedSport = action.payload;
      // Reset pitch when sport changes
      state.selectedPitch = null;
    },
    setSelectedPitch: (state, action) => {
      state.selectedPitch = action.payload;
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },
    filterSchedule: (state) => {
      let filtered = [...state.scheduleList];
      
      if (state.selectedFacility) {
        filtered = filtered.filter(item => 
          item.slot.facility._id === state.selectedFacility._id
        );
      }
      
      if (state.selectedSport) {
        filtered = filtered.filter(item => 
          item.slot.sport._id === state.selectedSport._id
        );
      }
      
      if (state.statusFilter) {
        filtered.forEach((item, index) => {
          let detailFilter = item.detail.filter(element => {
            if (state.statusFilter === 'Booked') return element.booked;
            if (state.statusFilter === 'Available') return !element.booked && !element.blocked;
            if (state.statusFilter === 'Blocked') return element.blocked;
            return true;
          });
          filtered[index].detail = detailFilter;
        });
      }
      
      state.filterScheduleList = filtered;
    },
    clearFilters: (state) => {
      state.selectedFacility = null;
      state.selectedSport = null;
      state.selectedPitch = null;
      state.statusFilter = null;
      state.filterScheduleList = state.scheduleList;
    },
    setPitches: (state, action) => {
      state.pitches = action.payload;
    },
    setWeeklySlots: (state, action) => {
      state.weeklySlots = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllSlots.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllSlots.fulfilled, (state, action) => {
        state.loading = false;
        state.scheduleList = action.payload;
        state.filterScheduleList = action.payload;
      })
      .addCase(fetchAllSlots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchWeeklySlots.pending, (state) => {
        state.weeklyLoading = true;
      })
      .addCase(fetchWeeklySlots.fulfilled, (state, action) => {
        state.weeklyLoading = false;
        // Handle the response format from the /getByDate endpoint
        if (action.payload && action.payload.status && action.payload.slots) {
          state.weeklySlots = action.payload.slots;
        } else if (action.payload && action.payload.slot) {
          // Handle the old format for backward compatibility
          state.weeklySlots = [action.payload.slot];
        } else {
          state.weeklySlots = [];
        }
      })
      .addCase(fetchWeeklySlots.rejected, (state, action) => {
        state.weeklyLoading = false;
        state.error = action.payload;
        state.weeklySlots = [];
      })
      .addCase(fetchFacilitiesAndSports.fulfilled, (state, action) => {
        state.facilities = action.payload.facilities;
        state.sports = action.payload.sports;
      });
  }
});

export const { 
  setSelectedDate, 
  setSelectedFacility, 
  setSelectedSport,
  setSelectedPitch,
  setStatusFilter,
  filterSchedule,
  clearFilters,
  setPitches,
  setWeeklySlots
} = scheduleSlice.actions;

export default scheduleSlice.reducer;